import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import React from "react";
import { IconManager } from "../../../../../../../../components/core/IconManager/IconManager";
import { BodyTyp } from "../../../../../../../../components/Typography/Typography";
import { PATHS_LABEL } from "../../../../../../../../constants/Dictionary";
import {
  FontWeightEnum,
  IconSizeEnum,
  WithOptional,
} from "../../../../../../../../types/AppTypes";
import { theme } from "../../../../../../../../constants/StyledComponents";
import { ReactComponent as AddIcon } from "../../../../../../../../icons/add.svg";
import {
  ContainerTitleGradePath,
  StyledManagePaths,
  StyledManagePathsContainer,
} from "./PathsDnd.styled";
import { PathGradeConfigItem } from "../../../../../../../../types/api/configurations.type";
import { reorder } from "../../../../../../../../utils/list.utils";
import { GradePathItem } from "../../../../../../../../components/GradePathItem/PathGradeItem";
import { GenericPathGradeItem, TypeItemPrimary } from "../ManagePathsGrades";

export type NewPathGradeConfigItem = WithOptional<
  PathGradeConfigItem,
  "id"
> | null;

type PathsDndProps = {
  isEditable: boolean;
  pathItems: GenericPathGradeItem[];
  setPathItems: (x: GenericPathGradeItem[]) => void;
  setPathGradeItems: (
    x: (prev: NewPathGradeConfigItem[][]) => NewPathGradeConfigItem[][],
  ) => void;
  setNewItemIds: (x: number | null) => void;
  newItemIds: number | null;
  setEditingId: (
    x: {
      id: number;
      type: TypeItemPrimary;
    } | null,
  ) => void;
  editingId: {
    id: number;
    type: string;
  } | null;
  handleEdit: (id: number, typeItemPrimary: TypeItemPrimary) => void;
  handleSave: (id: number, typeItemPrimary: TypeItemPrimary) => void;
  handleDelete: (id: number, typeItemPrimary: TypeItemPrimary) => void;
  handleInputChange: (text: string) => void;
  setNewNamePrimary: (text: string) => void;
  newNamePrimary: string;
  setIsUpdatingAnItem: (value: boolean) => void;
  isUpdatingAnItem: boolean;
  scrollRightContainer: () => void;
  handleCancel: (
    item: GenericPathGradeItem,
    type: TypeItemPrimary,
    isNewItem: boolean,
  ) => void;
  editingChildrenItem: boolean;
  activeChildrenItem: boolean;
};

export const PathsDnd = ({
  isEditable,
  pathItems,
  setPathItems,
  setPathGradeItems,
  newItemIds,
  setNewItemIds,
  setEditingId,
  editingId,
  handleEdit,
  handleSave,
  handleDelete,
  handleInputChange,
  setNewNamePrimary,
  newNamePrimary,
  setIsUpdatingAnItem,
  isUpdatingAnItem,
  scrollRightContainer,
  handleCancel,
  editingChildrenItem,
  activeChildrenItem,
}: PathsDndProps) => {
  const onDragPathsEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }
    const reorderedItems = reorder(
      pathItems,
      result.source.index,
      result.destination.index,
    );
    setPathItems(reorderedItems);
  };

  const handleCreateNewPath = () => {
    const lastId =
      pathItems.length > 0 ? pathItems[pathItems.length - 1].id + 1 : 0;

    const newElement: GenericPathGradeItem = {
      id: lastId,
      name: "",
      position: pathItems.length + 1,
    };

    setNewItemIds(lastId);
    setPathItems([...pathItems, newElement]);
    setNewNamePrimary("");
    setEditingId({ id: newElement.id, type: TypeItemPrimary.PATH });
    setPathGradeItems((prevItems: NewPathGradeConfigItem[][]) => {
      const nItems = prevItems[0]?.length || 0;
      const newNullArray: null[] = Array(nItems).fill(null);
      return [...prevItems, newNullArray];
    });
    setIsUpdatingAnItem(true);
    scrollRightContainer();
  };

  return (
    <StyledManagePaths>
      <ContainerTitleGradePath>
        <BodyTyp variant="1" fontWeight={FontWeightEnum.SEMIBOLD}>
          {PATHS_LABEL}
        </BodyTyp>
        {isEditable && (
          <IconManager
            size={IconSizeEnum.SMALL}
            onClick={!isUpdatingAnItem ? handleCreateNewPath : undefined}
            color={!isUpdatingAnItem ? theme.colors.black : theme.colors.grey_3}
          >
            <AddIcon />
          </IconManager>
        )}
      </ContainerTitleGradePath>
      <DragDropContext onDragEnd={onDragPathsEnd}>
        <Droppable droppableId="droppable" direction="horizontal">
          {(provided, snapshot) => (
            <StyledManagePathsContainer
              {...provided.droppableProps}
              ref={provided.innerRef}
              $isDraggingOver={snapshot.isDraggingOver}
            >
              {pathItems.map((item, index) => (
                <Draggable
                  key={item.id.toString()}
                  draggableId={item.id.toString()}
                  index={index}
                  isDragDisabled={!isEditable}
                >
                  {(provided, snapshot) => (
                    <GradePathItem
                      index={index}
                      provided={provided}
                      snapshot={snapshot}
                      editingId={editingId}
                      isEditable={isEditable}
                      newItemIds={newItemIds}
                      item={item}
                      newItemName={newNamePrimary}
                      handleEdit={handleEdit}
                      handleSave={handleSave}
                      handleDelete={handleDelete}
                      handleInputChange={handleInputChange}
                      typeItemPrimary={TypeItemPrimary.PATH}
                      isUpdatingAnItem={isUpdatingAnItem}
                      handleCancel={handleCancel}
                      editingChildrenItem={editingChildrenItem}
                      activeChildrenItem={activeChildrenItem}
                    />
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </StyledManagePathsContainer>
          )}
        </Droppable>
      </DragDropContext>
    </StyledManagePaths>
  );
};
